import { style } from '@angular/animations';
import { ClientEntity } from './../../models/clients';
import { PercentagePipe } from 'src/app/utils/percentage.pipe';
import { DatePipe, DecimalPipe } from '@angular/common';
import { BaseXLSReportService } from './base.excel-report.service';
import { ExposureTreeNode, SecurityTypeName } from '../../models/holdings';
import { Store } from '@ngrx/store';
import { Injectable } from "@angular/core";
import * as Excel from "exceljs";

@Injectable()
export class HoldingsExcelReportService extends BaseXLSReportService {

    constructor(public store: Store<any>, public decimalPipe: DecimalPipe, public percentagePipe: PercentagePipe, public datePipe: DatePipe) {
        super(store, decimalPipe, percentagePipe, datePipe);
    }

    exportHoldingsToExcel() {
        const workbook: Excel.Workbook = new Excel.Workbook();
        // this is a workaround: https://github.com/guyonroche/exceljs/issues/348#issuecomment-320690232
        workbook.creator = 'Adminis';
        workbook.created = new Date();
        workbook.modified = new Date();

        const exposuresSheet: Excel.Worksheet = workbook.addWorksheet('Exposures');
        this.createExposuresSheet(exposuresSheet);

        if (this.holdingsValuationData !== undefined && this.holdingsValuationData.length > 0) {
            const valuationsSheet: Excel.Worksheet = workbook.addWorksheet('Valuations');
            this.createValuationsSheet(valuationsSheet);
        }

        const date = this.datePipe.transform(new Date(this.selectedDate), 'd MMM yyyy');
        const reportFileName = this.selectedEntities[0].Name + '_Holdings_' + date + '.xlsx';
        this.writeSheet(workbook, reportFileName);
    }

    createExposuresSheet(worksheet: Excel.Worksheet) {
        worksheet.columns = [
            { header: '', key: '', width: 40 },
            { header: '', key: '', width: 25 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 },
            { header: '', key: '', width: 20 }
        ];
        this.createEntityTable(worksheet);

    }

    createValuationsSheet(worksheet: Excel.Worksheet) {
        let rowIndex = 0;
        worksheet.columns = [
            { header: 'Holdings Valuations', key: '', width: 40 }, // Asset Allocation
            { header: '', key: '', width: 40 }, // Name
            { header: '', key: '', width: 10 }, // Currency
            { header: '', key: '', width: 20, style: { numFmt: this.getPercentageFormat({ decimalPlaces: 2 }) } }, // Rate
            { header: '', key: '', width: 20 }, // Type
            { header: '', key: '', width: 20 }, // Maturity
            { header: '', key: '', width: 20, style: { numFmt: this.getPercentageFormat({ decimalPlaces: 2 }) } }, // YTM
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Duration
            { header: '', key: '', width: 20 }, // Next Date
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Accured Interest
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Next Coupon
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Premium
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Qty
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 4 }) } }, // Cost Price
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 4 }) } }, // Price
            { header: '', key: '', width: 20 }, // Price Date
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Mkt Value
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ decimalPlaces: 4 }) } },  // FX Rate
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Value
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Unrealised Value
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Realised Value
            { header: '', key: '', width: 20, style: { numFmt: this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 }) } }, // Income
            { header: '', key: '', width: 20, style: { numFmt: this.getPercentageFormat({ decimalPlaces: 2 }) } }   // weight
        ];
        rowIndex++;

        const row = worksheet.getRow(rowIndex);
        row.font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };

        worksheet.addRow([
            'Asset Allocation Category',
            'Name',
            'Currency',
            'Rate',
            'Type',
            'Maturity',
            'YTM',
            'Duration',
            'Next Date',
            'Accrued Interest',
            'Next Coupon',
            'Premium',
            'Qty',
            'Cost Price',
            'Price',
            'Price Date',
            'Mkt Value',
            'FX Rate',
            'Value',
            'Unrealised Value',
            'Realised Value',
            'Income',
            'Weight'
        ]);

        rowIndex++;


        for (let i = 0; i < worksheet.columns.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex);
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };
        }

        this.createValuationsTable(worksheet, rowIndex);

    }

    createValuationsTable(worksheet: Excel.Worksheet, rowIndex: number) {
        for (let i = 0; i < this.holdingsValuationData.length; i++) {
            const item = this.holdingsValuationData[i];
            const valuationsRowValues = [
                item['SecurityClassification'],
                item['Name'],
                item['CurrencyCode'],
                this.createCouponRateRowItem(item),
                item['CouponTypeName'],
                this.createMaturityDateRowItem(item),
                this.createYieldToMaturityRowItem(item),
                this.createDurationRowItem(item),
                this.createNextDateRowItem(item),
                this.createAccruedInterestRowItem(item),
                this.createNextCouponRowItem(item),
                this.createPremiumRowItem(item),
                this.createQtyRowItem(item),
                this.createCostPriceRowItem(item),
                this.createPriceRowItem(item),
                this.createPriceDateRowItem(item),
                this.createMarketValueRowItem(item),
                this.createFxRateRowItem(item),
                this.createRefMarketValueRowItem(item),
                this.createBaseUnrealisedValueRowItem(item),
                this.createRealisedValueRowItem(item),
                this.createIncomeRowItem(item),
                this.createWeightRowItem(item),
            ];

            worksheet.addRow(valuationsRowValues).font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: false,
                color: { argb: '000000' }
            };

            rowIndex++;
        }

        for (let k = 0; k < worksheet.columns.length; k++) {
            const rowNumberString = this.holdingsValuationData.length + 2;
            worksheet.getCell(this.xlsColumns[k] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };
        }
    }

    createEntityTable(worksheet: Excel.Worksheet) {
        let rowIndex = 1;

        if (this.allClientEntities !== undefined && this.allClientEntities.length > 0) {
            worksheet.addRow(['NAV']);
            rowIndex++;
            const row = worksheet.getRow(2);
            row.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: '000000' }
            };

            worksheet.addRow(['Entity', '', 'Total']);
            rowIndex++;
            for (let i = 0; i < 3; i++) {
                const cell = worksheet.getCell(this.xlsColumns[i] + rowIndex);
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: '80BD01' },
                    bgColor: { argb: '80BD01' }
                };

                cell.font = {
                    name: 'Segoe UI',
                    family: 2,
                    size: 9,
                    underline: false,
                    bold: true,
                    color: { argb: 'ffffff' }
                };
            }

            for (let i = 0; i < this.allClientEntities.length; i++) {
                const entity = this.allClientEntities[i];
                const moneyValueLabel = this.getMoneyValueLabel(entity);
                const currencyValue = (entity !== undefined && entity.NetAssetValue !== undefined && entity.NetAssetValue.Value !== undefined) ? this.getCurrency(entity.NetAssetValue.Value) : '';
                worksheet.addRow([entity.Name, moneyValueLabel, currencyValue]).font = {
                    name: 'Segoe UI',
                    family: 2,
                    size: 9,
                    underline: false,
                    bold: false,
                    color: { argb: '000000' }
                };

                const perValue = this.percentageNumber((entity.NetAssetValue.Value / this.totalValue), 4);

                worksheet.addRow([entity.Name, 'Actual %', perValue]).font = {
                    name: 'Segoe UI',
                    family: 2,
                    size: 9,
                    underline: false,
                    bold: false,
                    color: { argb: '000000' }
                };

            
                worksheet.getCell(rowIndex + 1, 3).numFmt = this.getNumberFormat({ includeSeparator: true, decimalPlaces: 2 });
                worksheet.getCell(rowIndex + 2, 3).numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                
                rowIndex += 2;

                for (let j = 0; j < 3; j++) {
                    const rowNumberString = rowIndex.toString();
                    if (i === this.allClientEntities.length - 1) {
                        worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                            bottom: { style: 'medium', color: { argb: '000000' } }
                        };
                    } else {
                        worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                            bottom: { style: 'thin', color: { argb: '000000' } }
                        };
                    }
                }
            }
        }

        if (this.assetAllocationData !== undefined && this.assetAllocationData.length > 0) {
            this.createAssetAllocationsTable(worksheet, rowIndex);
        }

    }

    getMoneyValueLabel(entity: ClientEntity) {
        if (entity !== undefined && entity.NetAssetValue !== undefined) {
            if (entity.NetAssetValue.CurrencyCode) {
                return `Actual ${entity.NetAssetValue.CurrencyCode}`;
            } else if (entity.NetAssetValue.Currency && typeof entity.NetAssetValue.Currency === 'string') {
                return `Actual ${entity.NetAssetValue.Currency}`;
            } else {
                return 'Actual Value';
            }
        } else {
            return 'Actual Value';
        }
    }


    createAssetAllocationsTable(worksheet: Excel.Worksheet, rowIndex: number) {
        rowIndex++;
        worksheet.addRow([]);
        rowIndex++;
        worksheet.addRow(['Asset Allocations']).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };
        rowIndex++;
        const assetAllocationsHeaders = [];
        assetAllocationsHeaders.push('');

        for (let i = 0; i < this.assetAllocationData.length; i++) {
            assetAllocationsHeaders.push(this.assetAllocationData[i].ClassificationName);
        }
        worksheet.addRow(assetAllocationsHeaders);

        for (let i = 0; i < assetAllocationsHeaders.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + (rowIndex).toString());
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };

        }

        rowIndex++;

        // Not to be deleted.Temporarily commented out because dollar values are incorrectly calculated.

        // const dollarValues: any[] = this.assetAllocationData.map((item) => {
        //     return (item !== undefined && item.Total !== undefined) ? this.getCurrency(item.Total) : '';
        // });
        // dollarValues.unshift('Actual $');

        // worksheet.addRow(dollarValues).font = {
        //     name: 'Segoe UI',
        //     family: 2,
        //     size: 9,
        //     underline: false,
        //     bold: false,
        //     color: { argb: '000000' }
        // };
        // rowIndex++;

        const percValues: any[] = this.assetAllocationData.map((item) => {
            return (item !== undefined && item.Actual !== undefined) ? this.percentageNumber(item.Actual, 4) : '';
        });
        percValues.unshift('Actual %');

        worksheet.addRow(percValues).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: false,
            color: { argb: '000000' }
        };
        

        for (let j = 0; j < assetAllocationsHeaders.length; j++) {
            const rowNumberString = rowIndex.toString();
            worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                bottom: { style: 'medium', color: { argb: '000000' } }
            };

            if(j != 0){
                worksheet.getCell(this.xlsColumns[j] + rowNumberString).numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
            }
        }

        if (this.exposuresCurrencyData !== undefined && this.exposuresCurrencyData.children !== undefined && this.exposuresCurrencyData.children.length > 0) {
            rowIndex = this.createExposureTable(worksheet, this.exposuresCurrencyData.children, 'Currency Exposure', rowIndex);
        }
        if (this.maturityProfileData !== undefined && this.maturityProfileData.children !== undefined && this.maturityProfileData.children.length > 0) {
            rowIndex = this.createExposureTable(worksheet, this.maturityProfileData.children, 'Maturity Profile', rowIndex);
        }
    }

    createExposureTable(worksheet: Excel.Worksheet, data: ExposureTreeNode[], title: string, rowIndex: number) {
        rowIndex += 2;

        const entitiesDataObject: {
            [key: string]: {
                data: {
                    caption: string;
                    value: number;
                    weight: number;
                }
            }[]
        } = {};

        const selectedClientEntities: ClientEntity[] = this.allClientEntities.filter((e: ClientEntity) => this.selectedEntities.map((selectedEntity: ClientEntity) => selectedEntity.EntityId === e.EntityId));
        selectedClientEntities.forEach((entity: ClientEntity) => {
            entitiesDataObject[entity.Name] = [];
        });

        for (let i = 0; i < data.length; i++) {
            selectedClientEntities.forEach((entity: ClientEntity) => {
                const entityValue: any = data[i].children.find((child) => {
                    return child.data.caption === entity.Name;
                });
                entitiesDataObject[entity.Name].push(entityValue);
            });
        }

        worksheet.addRow([]);
        worksheet.addRow([title]).font = {
            name: 'Segoe UI',
            family: 2,
            size: 9,
            underline: false,
            bold: true,
            color: { argb: '000000' }
        };

        const exposuresHeaders = ['Entity', ''];

        for (let i = 0; i < data.length; i++) {
            exposuresHeaders.push(data[i].data.caption);
        }

        worksheet.addRow(exposuresHeaders);
        rowIndex++;

        for (let i = 0; i < exposuresHeaders.length; i++) {
            const cell = worksheet.getCell(this.xlsColumns[i] + (rowIndex).toString());
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: '80BD01' },
                bgColor: { argb: '80BD01' }
            };

            cell.font = {
                name: 'Segoe UI',
                family: 2,
                size: 9,
                underline: false,
                bold: true,
                color: { argb: 'ffffff' }
            };
        }

        let entityCount = 0;
        for (const key in entitiesDataObject) {
            if (entitiesDataObject.hasOwnProperty(key)) {
                // Do not remove: temporarily commented out since numbers don't add up

                // const entityActualDollarDataArray = [];
                // entityActualDollarDataArray.push(key);
                // entityActualDollarDataArray.push('');
                // for (let i = 0; i < entitiesDataObject[key].length; i++) {
                //     let value = (entitiesDataObject[key][i] !== undefined && entitiesDataObject[key][i].data.value) ? entitiesDataObject[key][i].data.value : '';
                //     value = (value !== '') ? this.getCurrency(Number(value)) : '';
                //     entityActualDollarDataArray.push(value);
                // }

                // worksheet.addRow(entityActualDollarDataArray).font = {
                //     name: 'Segoe UI',
                //     family: 2,
                //     size: 9,
                //     underline: false,
                //     bold: false,
                //     color: { argb: '000000' }
                // };
                // rowIndex++;

                const entityActualPercentageDataArray = [];
                entityActualPercentageDataArray.push(key);
                entityActualPercentageDataArray.push('');
                for (let i = 0; i < entitiesDataObject[key].length; i++) {
                    const weight = (entitiesDataObject[key][i] !== undefined && entitiesDataObject[key][i].data.weight) ? this.percentageNumber((entitiesDataObject[key][i].data.weight), 4) : '';
                    entityActualPercentageDataArray.push(weight);
                }

                worksheet.addRow(entityActualPercentageDataArray).font = {
                    name: 'Segoe UI',
                    family: 2,
                    size: 9,
                    underline: false,
                    bold: false,
                    color: { argb: '000000' }
                };
                rowIndex++;

                for (let j = 0; j < exposuresHeaders.length; j++) {
                    const rowNumberString = rowIndex.toString();
                    if (entityCount === this.selectedEntities.length - 1) {
                        worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                            bottom: { style: 'medium', color: { argb: '000000' } }
                        };
                    } else {
                        worksheet.getCell(this.xlsColumns[j] + rowNumberString).border = {
                            bottom: { style: 'thin', color: { argb: '000000' } }
                        };
                    }
                    
                    if(j > 1){
                        worksheet.getCell(this.xlsColumns[j] + rowNumberString).numFmt = this.getPercentageFormat({ decimalPlaces: 2 });
                    }
                }

                entityCount++;

            }
        }

        return rowIndex;

    }

    createCouponRateRowItem(rowItem: any){
        if (rowItem['CouponRate'] === null){
            return null;
        }
        
        return this.percentageNumber(rowItem['CouponRate'], 4);
    }

    createMaturityDateRowItem(rowItem: any){
        if(rowItem['SecurityTypeName'] !== SecurityTypeName.FIXED_INCOME) {
            return '';
        }

        if (rowItem['MaturityDate'] !== null) {
            return new Date(rowItem['MaturityDate']).toDateString();
        }
            
        return 'Perpetual';
    }

    createYieldToMaturityRowItem(rowItem: any) {
        if (rowItem['YieldToMaturity'] === null || rowItem['SecurityTypeName'] !== SecurityTypeName.FIXED_INCOME) {
            return '';
        }

        return this.percentageNumber(rowItem['YieldToMaturity'], 4);
    }

    createDurationRowItem(rowItem: any) {
        if (rowItem['Duration'] !== null && rowItem['SecurityTypeName'] === SecurityTypeName.FIXED_INCOME) {
            return this.decimalNumber(rowItem['Duration'], 2);
        }

        return '';
    }

    createNextDateRowItem(rowItem: any) {
        if (rowItem['NextDate'] === null) {
            return '';
        }

        return new Date(rowItem['NextDate']).toDateString();
    }

    createAccruedInterestRowItem(rowItem: any) {
        if (rowItem['AccruedInterest'] === null) {
            return '';
        }

        return this.decimalNumber(rowItem['AccruedInterest'].Value, 2);
    }

    createNextCouponRowItem(rowItem: any) {
        if (rowItem['NextCoupon'] === null) {
            return '';
        }

        return this.decimalNumber(rowItem['NextCoupon'].Value, 2)
    }

    createPremiumRowItem(rowItem: any) {
        if (rowItem['Premium'] === null) {
            return '';
        }

        return this.decimalNumber(rowItem['Premium'].Value, 2);
    }

    createQtyRowItem(rowItem: any) {
        if (rowItem['Qty'] === null || rowItem['Qty'] === 0 || rowItem['PriceDate'] === null) {
            return '';
        }

        return this.decimalNumber(rowItem['Qty'], 4);
    }

    createCostPriceRowItem(rowItem: any) {
        if (rowItem['CostPrice'] === null || rowItem['CostPrice'].Value === null || rowItem['PriceDate'] === null || rowItem['Qty'] === 0) {
            return '';
        }

        return this.decimalNumber(rowItem['CostPrice'].Value, 4);
    }

    createPriceRowItem(rowItem: any) {
        if (rowItem['Price'] === null || rowItem['PriceDate'] === null) {
            return '';
        }
        
        return this.decimalNumber(rowItem['Price'], 4)
    }

    createPriceDateRowItem(rowItem: any) {
        if (rowItem['PriceDate'] === null) {
            return '';
        }
        
        return new Date(rowItem['PriceDate']).toDateString();
    }

    createMarketValueRowItem(rowItem: any) {
        if (rowItem['MarketValue'] === null || rowItem['MarketValue'].Value === null) {
            return '';
        }

        return this.decimalNumber(rowItem['MarketValue'].Value, 2);
    }

    createFxRateRowItem(rowItem: any) {
        if (rowItem['FxRate'] === null || rowItem['FxRate'].Value === null) {
            return '';
        }

        return this.decimalNumber(rowItem['FxRate'], 4);
    }

    createRefMarketValueRowItem(rowItem: any) {
        if (rowItem['RefMarketValue'] === null || rowItem['RefMarketValue'].Value === null) {
            return '';
        }

        return this.decimalNumber(rowItem['RefMarketValue'].Value, 2);
    }

    createBaseUnrealisedValueRowItem(rowItem: any) {
        if (rowItem['BaseUnrealisedValue'] === null || rowItem['BaseUnrealisedValue'].Value === null) {
            return '';
        }

        return this.decimalNumber(rowItem['BaseUnrealisedValue'].Value, 2);
    }

    createRealisedValueRowItem(rowItem: any) {
        if (rowItem['RealisedValue'] === null || rowItem['RealisedValue'].Value === null) {
            return null;
        }

        return this.decimalNumber(rowItem['RealisedValue'].Value, 2);
    }
    
    createIncomeRowItem(rowItem: any) {
        if (rowItem['Income'] === null || rowItem['Income'].Value === null) {
            return '';
        }

        return this.decimalNumber(rowItem['Income'].Value, 2);
    }

    createWeightRowItem(rowItem: any) {
        if (rowItem['Weight'] === null) {
            return '';
        }

        return this.percentageNumber(rowItem['Weight'], 4);
    }
}
